import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Activity, BarChartHorizontal, Coins, Layers, ShieldAlert, TrendingUp } from 'lucide-react';
import RiskDashboardScreen from './RiskDashboardScreen';
import PerformanceAttributionScreen from './PerformanceAttributionScreen';
import PikExposureScreen from './PikExposureScreen';
import RiskCenterScreen from './risk/RiskCenterScreen';
import { AxiomMetricCard, AxiomMetricGrid, AxiomStatusBadge } from './shared';
import {
  CollapsedRail,
  CollapsedRailItem,
  CollapsedRailSection,
  Eyebrow,
  HeroSubtitle,
  HeroTitle,
  HeroTop,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  RailItem,
  RailItemDescription,
  RailItemLabel,
  RailSection,
  RailSectionLabel,
  WorkspaceBody,
  WorkspaceHero,
  WorkspaceMain,
  WorkspacePanel,
  WorkspaceRail,
  WorkspaceRailToggle,
  WorkspaceShell,
} from './shared/Workspace';

type TabType = 'risk' | 'cross-asset' | 'attribution' | 'pik';

const AnalyticsScreen: React.FC = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TabType>('risk');
  const [railCollapsed, setRailCollapsed] = useState(false);

  useEffect(() => {
    if (location.pathname === '/risk-dashboard' || location.pathname === '/analytics/risk') {
      setActiveTab('risk');
    } else if (location.pathname === '/performance-attribution' || location.pathname === '/analytics/attribution') {
      setActiveTab('attribution');
    } else if (location.pathname === '/analytics/pik') {
      setActiveTab('pik');
    } else if (
      location.pathname === '/risk' ||
      location.pathname.startsWith('/risk/') ||
      location.pathname === '/analytics/cross-asset'
    ) {
      setActiveTab('cross-asset');
    }
  }, [location.pathname]);

  const tabs: Array<{
    id: TabType;
    label: string;
    description: string;
    icon: React.ReactNode;
    badge: string;
    tone: 'cyan' | 'violet' | 'emerald' | 'amber';
  }> = [
    {
      id: 'risk',
      label: 'Risk Analytics',
      description: 'Risk dashboard, factor surveillance and current alerts.',
      icon: <ShieldAlert />,
      badge: 'Risk',
      tone: 'amber',
    },
    {
      id: 'cross-asset',
      label: 'Cross-Asset Risk',
      description: 'VaR, stress, exposures and concentrations across public and private books.',
      icon: <Layers />,
      badge: 'VaR',
      tone: 'cyan',
    },
    {
      id: 'attribution',
      label: 'Performance Attribution',
      description: 'Return drivers, factor contribution and period attribution.',
      icon: <TrendingUp />,
      badge: 'TWR',
      tone: 'emerald',
    },
    {
      id: 'pik',
      label: 'PIK Exposure',
      description: 'Payment-in-kind accrual, income and non-accrual overlap across the credit book.',
      icon: <Coins />,
      badge: 'PIK',
      tone: 'violet',
    },
  ];
  const activeMeta = tabs.find(tab => tab.id === activeTab) || tabs[0];

  return (
    <WorkspaceShell>
      <WorkspaceHero>
        <HeroTop>
          <div>
            <Eyebrow $tone="violet">Analytics Workspace</Eyebrow>
            <HeroTitle>Analytics & Risk Command</HeroTitle>
            <HeroSubtitle>
              Cross-asset risk, stress, factor exposure and performance attribution in one institutional workspace.
              The workspace lens changes the analysis surface without splitting risk into disconnected pages.
            </HeroSubtitle>
          </div>
          <AxiomStatusBadge $tone="emerald">
            <Activity className="h-3 w-3" />
            Live Engine
          </AxiomStatusBadge>
        </HeroTop>
        <AxiomMetricGrid style={{ marginTop: '1rem' }}>
          <AxiomMetricCard label="Risk Engine" value="VaR / Stress" subtext="Scenario and factor analytics" icon={<ShieldAlert />} tone="amber" />
          <AxiomMetricCard label="Coverage" value="Cross-Asset" subtext="Public, private and structured credit" icon={<Layers />} tone="cyan" />
          <AxiomMetricCard label="Attribution" value="Performance" subtext="Return drivers and contribution" icon={<BarChartHorizontal />} tone="emerald" />
        </AxiomMetricGrid>
      </WorkspaceHero>

      <WorkspaceBody $railCollapsed={railCollapsed}>
        <WorkspaceRail $collapsed={railCollapsed}>
          <WorkspaceRailToggle collapsed={railCollapsed} onToggle={() => setRailCollapsed(prev => !prev)} />
          {railCollapsed ? (
            <CollapsedRail>
              <CollapsedRailSection>
                {tabs.map(tab => (
                  <CollapsedRailItem
                    key={tab.id}
                    $active={activeTab === tab.id}
                    $tone={tab.tone}
                    aria-label={tab.label}
                    title={`${tab.label} - ${tab.description}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.icon}
                  </CollapsedRailItem>
                ))}
              </CollapsedRailSection>
            </CollapsedRail>
          ) : (
            <RailSection>
              <RailSectionLabel>Risk Workbenches</RailSectionLabel>
              {tabs.map(tab => (
                <RailItem
                  key={tab.id}
                  $active={activeTab === tab.id}
                  $tone={tab.tone}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.icon}
                  <div>
                    <RailItemLabel>{tab.label}</RailItemLabel>
                    <RailItemDescription>{tab.description}</RailItemDescription>
                  </div>
                </RailItem>
              ))}
            </RailSection>
          )}
        </WorkspaceRail>

        <WorkspaceMain>
          <WorkspacePanel>
            <PanelHeader>
              <div>
                <PanelTitle>{activeMeta.label}</PanelTitle>
                <PanelSubtitle>{activeMeta.description}</PanelSubtitle>
              </div>
              <AxiomStatusBadge $tone={activeMeta.tone}>{activeMeta.badge}</AxiomStatusBadge>
            </PanelHeader>
            <PanelBody>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {activeTab === 'risk' && <RiskDashboardScreen />}
                  {activeTab === 'cross-asset' && <RiskCenterScreen embedded />}
                  {activeTab === 'attribution' && <PerformanceAttributionScreen />}
                  {activeTab === 'pik' && <PikExposureScreen />}
                </motion.div>
              </AnimatePresence>
            </PanelBody>
          </WorkspacePanel>
        </WorkspaceMain>
      </WorkspaceBody>
    </WorkspaceShell>
  );
};

export default AnalyticsScreen;
